import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, Building2, User, Calendar, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { adminAxios } from '../lib/adminApi';

export const AdminInquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const response = await adminAxios.get(`/wholesale-inquiries`);
      setInquiries(response.data);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (inquiryId, status) => {
    try {
      await adminAxios.put(`/wholesale-inquiries/${inquiryId}?status=${status}`);
      toast.success(`Inquiry marked as ${status}`);
      fetchInquiries();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredInquiries = filter === 'all' 
    ? inquiries 
    : inquiries.filter(inq => inq.status === filter);

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <p className="text-muted-foreground">Loading inquiries...</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-inquiries-page" className="min-h-screen pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-12">
          <h1 className="font-display text-5xl md:text-6xl font-normal tracking-tight text-foreground mb-4">
            Wholesale <span className="italic">Inquiries</span>
          </h1>
          <p className="text-muted-foreground">
            Total inquiries: {inquiries.length} | New: {inquiries.filter(i => i.status === 'new').length}
          </p>
        </div>

        <div className="flex space-x-4 mb-8">
          <button
            onClick={() => setFilter('all')}
            className={`px-6 py-2 rounded-full text-sm uppercase tracking-widest transition-all duration-300 ${
              filter === 'all'
                ? 'bg-primary text-primary-foreground'
                : 'border border-primary/30 text-primary hover:bg-primary/10'
            }`}
          >
            All ({inquiries.length})
          </button>
          <button
            onClick={() => setFilter('new')}
            className={`px-6 py-2 rounded-full text-sm uppercase tracking-widest transition-all duration-300 ${
              filter === 'new'
                ? 'bg-primary text-primary-foreground'
                : 'border border-primary/30 text-primary hover:bg-primary/10'
            }`}
          >
            New ({inquiries.filter(i => i.status === 'new').length})
          </button>
          <button
            onClick={() => setFilter('contacted')}
            className={`px-6 py-2 rounded-full text-sm uppercase tracking-widest transition-all duration-300 ${
              filter === 'contacted'
                ? 'bg-primary text-primary-foreground'
                : 'border border-primary/30 text-primary hover:bg-primary/10'
            }`}
          >
            Contacted ({inquiries.filter(i => i.status === 'contacted').length})
          </button>
          <button
            onClick={() => setFilter('completed')}
            className={`px-6 py-2 rounded-full text-sm uppercase tracking-widest transition-all duration-300 ${
              filter === 'completed'
                ? 'bg-primary text-primary-foreground'
                : 'border border-primary/30 text-primary hover:bg-primary/10'
            }`}
          >
            Completed ({inquiries.filter(i => i.status === 'completed').length})
          </button>
        </div>

        {filteredInquiries.length === 0 ? (
          <div className="text-center py-24 border border-white/5 rounded-lg">
            <p className="text-muted-foreground">No inquiries found</p>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredInquiries.map((inquiry) => (
              <motion.div
                key={inquiry.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-white/10 p-8 hover:border-primary/30 transition-colors duration-300"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    {inquiry.status === 'new' && (
                      <span className="px-3 py-1 bg-primary/20 text-primary text-xs uppercase tracking-widest rounded-full flex items-center space-x-1">
                        <Clock size={12} />
                        <span>New</span>
                      </span>
                    )}
                    {inquiry.status === 'contacted' && (
                      <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-xs uppercase tracking-widest rounded-full">
                        Contacted
                      </span>
                    )}
                    {inquiry.status === 'completed' && (
                      <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs uppercase tracking-widest rounded-full flex items-center space-x-1">
                        <CheckCircle size={12} />
                        <span>Completed</span>
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Calendar size={14} />
                    <span>{formatDate(inquiry.created_at)}</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <Building2 size={16} className="text-primary" />
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-widest">Company</p>
                        <p className="font-medium">{inquiry.company_name}</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <User size={16} className="text-primary" />
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-widest">Contact Person</p>
                        <p className="font-medium">{inquiry.contact_person}</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <Mail size={16} className="text-primary" />
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-widest">Email</p>
                        <a 
                          href={`mailto:${inquiry.email}`}
                          className="font-medium hover:text-primary transition-colors"
                        >
                          {inquiry.email}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <Phone size={16} className="text-primary" />
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-widest">Phone</p>
                        <a 
                          href={`tel:${inquiry.phone}`}
                          className="font-medium hover:text-primary transition-colors"
                        >
                          {inquiry.phone}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-white/10 pt-6 mb-6">
                  <p className="text-xs text-muted-foreground uppercase tracking-widest mb-2">Message</p>
                  <p className="text-sm leading-relaxed">{inquiry.message}</p>
                </div>

                <div className="flex space-x-3">
                  {inquiry.status === 'new' && (
                    <button
                      onClick={() => updateStatus(inquiry.id, 'contacted')}
                      className="px-4 py-2 bg-primary/10 text-primary hover:bg-primary/20 rounded text-sm uppercase tracking-widest transition-colors"
                    >
                      Mark as Contacted
                    </button>
                  )}
                  {inquiry.status === 'contacted' && (
                    <button
                      onClick={() => updateStatus(inquiry.id, 'completed')}
                      className="px-4 py-2 bg-green-500/10 text-green-400 hover:bg-green-500/20 rounded text-sm uppercase tracking-widest transition-colors"
                    >
                      Mark as Completed
                    </button>
                  )}
                  <a
                    href={`mailto:${inquiry.email}`}
                    className="px-4 py-2 border border-primary/30 text-primary hover:bg-primary/10 rounded text-sm uppercase tracking-widest transition-colors"
                  >
                    Send Email
                  </a>
                  <a
                    href={`tel:${inquiry.phone}`}
                    className="px-4 py-2 border border-primary/30 text-primary hover:bg-primary/10 rounded text-sm uppercase tracking-widest transition-colors"
                  >
                    Call
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
